/**
 * FogBugz toolset schemas (epic PI-SPEC-SW-E307, ER-1663, contract
 * fogbugz 1).
 *
 * The EVTX summary on an existing case runs in three steps:
 *
 * 1. SpecTree returns the deterministic digest of one `.evtx` attachment
 *    ({@link fogbugzEvtxDigestResponseSchema}); no AI runs on the server.
 * 2. The page asks the Toolbox to summarize it
 *    (`spectreeToolbox.fogbugz.summarizeEvtx`, input
 *    {@link fogbugzEvtxSummarizeInputSchema}); the person's own Claude Code
 *    prints one `SPECTREE_FOGBUGZ_EVTX {json}` line
 *    ({@link fogbugzEvtxTurnMarkerSchema}) with the four sections.
 * 3. The person reviews the composed summary and SpecTree posts it to the
 *    case ({@link fogbugzEvtxReviewedPostBodySchema}), refusing when its own
 *    fresh digest is not the one the person reviewed.
 *
 * The sections are untrusted model output: they cross the app/page/server
 * boundary as plain text, are capped, and are checked with
 * {@link hasAllEvtxSections} before they are shown or posted.
 */
import { z } from "zod";
import { FOGBUGZ_EVTX_DIGEST_MAX_CHARS, FOGBUGZ_EVTX_SECTIONS, FOGBUGZ_EVTX_SECTIONS_MAX_CHARS } from "./constants.js";
/** A FogBugz case number (`ixBug`). */
export const fogbugzCaseIdSchema = z.number().int().positive();
/** The summary sections text: the four level-2 sections, capped. */
export const fogbugzEvtxSectionsSchema = z.string().min(1).max(FOGBUGZ_EVTX_SECTIONS_MAX_CHARS);
/** The digest markdown, capped. */
export const fogbugzEvtxDigestMarkdownSchema = z.string().min(1).max(FOGBUGZ_EVTX_DIGEST_MAX_CHARS);
/** SHA-256 of the UTF-8 digest markdown, as 64 lowercase hex characters. */
export const fogbugzEvtxDigestSha256Schema = z.string().regex(/^[0-9a-f]{64}$/);
/** Record counts per Event Viewer level, as the digest reports them. */
export const fogbugzEvtxCountsByLevelSchema = z.object({
    Critical: z.number().int().min(0),
    Error: z.number().int().min(0),
    Warning: z.number().int().min(0),
    Information: z.number().int().min(0),
    Verbose: z.number().int().min(0),
    Unknown: z.number().int().min(0),
});
/**
 * What SpecTree returns for the deterministic digest of one `.evtx`
 * attachment on a case. `sURL` is the attachment URL exactly as the case
 * reports it; the reviewed post sends it back. `summaryFileName` is the name
 * the summary is attached under (`Lynx.evtx-summary.md`).
 */
export const fogbugzEvtxDigestResponseSchema = z.object({
    caseId: fogbugzCaseIdSchema,
    fileName: z.string().min(1),
    sURL: z.string().min(1),
    summaryFileName: z.string().min(1),
    digestMarkdown: fogbugzEvtxDigestMarkdownSchema,
    /** True when parsing stopped early at a record-count or time limit. */
    truncated: z.boolean(),
    digestStats: z.object({
        recordsSeen: z.number().int().min(0),
        recordsParsed: z.number().int().min(0),
        countsByLevel: fogbugzEvtxCountsByLevelSchema,
    }),
});
/** The input of `spectreeToolbox.fogbugz.summarizeEvtx`: what the engine hands Claude. */
export const fogbugzEvtxSummarizeInputSchema = z.object({
    caseId: fogbugzCaseIdSchema,
    fileName: z.string().min(1),
    digestMarkdown: fogbugzEvtxDigestMarkdownSchema,
});
/** The JSON after `SPECTREE_FOGBUGZ_EVTX` on the skill's result line. */
export const fogbugzEvtxTurnMarkerSchema = z.object({
    sections: fogbugzEvtxSectionsSchema,
});
/**
 * The body the page posts to SpecTree once the person has reviewed the
 * summary: which attachment it summarizes, the (possibly edited) sections and
 * `digestSha256`, the SHA-256 of the exact `digestMarkdown` the preview showed
 * ({@link fogbugzEvtxDigestSha256Schema}). SpecTree digests the attachment
 * again, refuses with 409 `DIGEST_CHANGED` when that digest's hash differs (the
 * parser stops at a time limit, so a second read can cover fewer records), and
 * otherwise composes the posted text with `composeToolboxEvtxSummary`, so the
 * preview and the posted attachment are identical.
 */
export const fogbugzEvtxReviewedPostBodySchema = z.object({
    sURL: z.string().min(1),
    sections: fogbugzEvtxSectionsSchema,
    digestSha256: fogbugzEvtxDigestSha256Schema,
});
/**
 * Every code the fogbugz engine reports when it cannot return summary
 * sections. `summarizeEvtx` resolves with one on failure; the page maps each
 * to a plain message.
 */
export const FOGBUGZ_ENGINE_ERROR_CODES = [
    /** Claude Code is not installed or cannot be found. */
    "CLAUDE_NOT_FOUND",
    "CLAUDE_NOT_SIGNED_IN",
    /** Claude Code would bill an API key rather than the person's subscription seat. */
    "API_KEY_BILLING",
    /** The person's Claude usage limit is reached; the message names the reset time. */
    "USAGE_LIMIT",
    /** The turn ran past `FOGBUGZ_EVTX_TURN_TIMEOUT_MS`. */
    "TIMED_OUT",
    /** The bundled plugin with the FogBugz EVTX summary skill is missing. */
    "PLUGIN_MISSING",
    /** Claude finished without a valid `SPECTREE_FOGBUGZ_EVTX` line with all four sections. */
    "NO_SUMMARY_RETURNED",
    /** The person canceled the summary. */
    "CANCELED",
    /** The app is already writing an EVTX summary (one at a time per app). */
    "BUSY",
    /**
     * Stopped before Claude started: Claude Code did not apply the read-only
     * settings (a write tool, Bash or AskUserQuestion listed), or the private
     * turn folder could not be made. Retrying does not help; the message says
     * what to fix.
     */
    "REFUSED",
    /** Claude Code could not reach Anthropic. */
    "NETWORK",
    /** Anything else; the message is the engine's plain sentence. */
    "UNKNOWN",
];
/** True when `value` is a fogbugz engine error code. */
export function isFogbugzEngineErrorCode(value) {
    return typeof value === "string" && FOGBUGZ_ENGINE_ERROR_CODES.includes(value);
}
/**
 * Every line, trimmed of trailing whitespace, with the lines of fenced code
 * blocks (fences included) blanked. Fences follow CommonMark: an opening fence
 * is up to three spaces of indentation, three or more backticks or tildes and
 * an optional info string (with no backtick after a backtick fence); it closes
 * at a line of up to three spaces, the same character at least as many times,
 * and nothing else. An unclosed fence runs to the end (`open`). Lines end at
 * CRLF, LF or a lone CR, as in CommonMark.
 */
function scanFences(markdown) {
    const out = [];
    let fence = null;
    for (const raw of markdown.split(/\r\n|\r|\n/)) {
        const line = raw.trimEnd();
        if (fence === null) {
            const open = /^ {0,3}(`{3,}|~{3,})(.*)$/.exec(line);
            const marker = open?.[1] ?? "";
            if (open && !(marker.startsWith("`") && (open[2] ?? "").includes("`"))) {
                fence = { char: marker.charAt(0), length: marker.length };
                out.push("");
                continue;
            }
            out.push(line);
            continue;
        }
        const close = /^ {0,3}(`{3,}|~{3,})$/.exec(line)?.[1];
        if (close?.startsWith(fence.char) && close.length >= fence.length)
            fence = null;
        out.push("");
    }
    return { lines: out, open: fence !== null };
}
/** An ATX heading of level 1 or 2 (up to three spaces of indentation), as `## Text`; else null. */
function topAtxHeading(line) {
    const match = /^ {0,3}(#{1,2})(?:[ \t]+(.*?))?(?:[ \t]+#+)?[ \t]*$/.exec(line);
    return match ? `${match[1] ?? ""} ${match[2] ?? ""}`.trimEnd() : null;
}
/**
 * True when `line` underlines `previous` as a setext heading (`Text` then
 * `===` or `---`): `previous` is paragraph text, not blank and not a heading,
 * thematic break, list item, quote or table row.
 */
function isSetextUnderline(line, previous) {
    if (!/^ {0,3}(?:=+|-+)[ \t]*$/.test(line) || previous.trim() === "")
        return false;
    if (/^ {0,3}([-*_=])(?:[ \t]*\1)*[ \t]*$/.test(previous))
        return false;
    return !/^ {0,3}(?:#{1,6}(?:[ \t]|$)|[-*+](?:[ \t]|$)|\d{1,9}[.)](?:[ \t]|$)|>|\|)/.test(previous);
}
/**
 * True when `markdown` carries exactly the `expected` level-2 headings, in
 * order, each once, and no other level-1 or level-2 heading (ATX or setext),
 * outside fenced code blocks. The one CommonMark-aware heading reader behind
 * {@link hasAllEvtxSections} and the "Look into the code" report check
 * (`hasAllCodeReportSections`), so untrusted model output is held to the
 * same rules in both. Headings may be indented up to three spaces; level-3
 * and deeper subheadings and other text between the headings are allowed.
 */
export function hasExactTopLevelHeadings(markdown, expected) {
    const { lines } = scanFences(markdown);
    const headings = [];
    for (const [index, line] of lines.entries()) {
        const atx = topAtxHeading(line);
        if (atx !== null)
            headings.push(atx);
        else if (index > 0 && isSetextUnderline(line, lines[index - 1] ?? ""))
            return false;
    }
    return headings.length === expected.length && expected.every((heading, index) => headings[index] === heading);
}
/**
 * True when `sections` carries the four {@link FOGBUGZ_EVTX_SECTIONS} headings,
 * in order, each once, and no other level-1 or level-2 heading, outside fenced
 * code blocks. So untrusted model output cannot add its own `# Event log
 * summary: …` or `## Digest` (ATX or setext) above the real ones. Headings may
 * be indented up to three spaces; level-3 and deeper subheadings and other
 * text between the headings are allowed. The sections must not end inside a
 * fenced code block: the composed summary puts `## Digest` after them, and an
 * open fence would swallow it.
 */
export function hasAllEvtxSections(sections) {
    return !scanFences(sections).open && hasExactTopLevelHeadings(sections, FOGBUGZ_EVTX_SECTIONS);
}
//# sourceMappingURL=schemas.js.map